// src/pages/HelperHistory.js
import { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { normalizeRole, ROLE_VALUE, getRoleLabel } from '../constants/roleLabels';
import { db } from '../firebase';
import {
  collection,
  onSnapshot,
  doc,
  getDoc,
  updateDoc,
  arrayUnion,
  query,
  orderBy,
} from 'firebase/firestore';
import { calculateSummaryStats } from '../services/historyService';
import OperationsShell from '../components/dashboard/shell/OperationsShell';
import IncidentHistoryView from '../components/dashboard/history/IncidentHistoryView';
import { logger } from "../utils/logger";

function HelperHistory() {
  const { currentUser, currentUserRole, logout, ready } = useAuth();
  const [completedTasks, setCompletedTasks] = useState([]);
  const [hiddenHistory, setHiddenHistory] = useState(null);
  const [stats, setStats] = useState({
    totalTasks: 0,
    avgRating: 0,
    monthlyData: [],
  });
  const [domains, setDomains] = useState([]);
  const navigate = useNavigate();

  // Load helper's hiddenHistory once
  useEffect(() => {
    if (!ready || !currentUser) return;

    const fetchHiddenHistory = async () => {
      try {
        const userRef = doc(db, 'users', currentUser.uid);
        const userSnap = await getDoc(userRef);
        if (userSnap.exists()) {
          setHiddenHistory(userSnap.data().hiddenHistory || []);
        } else {
          setHiddenHistory([]);
        }
      } catch (err) {
        console.error('Error fetching hiddenHistory:', err);
        setHiddenHistory([]);
      }
    };

    fetchHiddenHistory();
  }, [currentUser, ready]);

  // Subscribe to completed history from /users/{uid}/history
  useEffect(() => {
    if (!ready || !currentUser || normalizeRole(currentUserRole) !== ROLE_VALUE.specialist) return;

    // Wait until hiddenHistory is loaded to avoid premature filtering
    if (hiddenHistory === null) return;

    const historyRef = collection(db, `users/${currentUser.uid}/history`);
    const q = query(historyRef, orderBy('dateCompleted', 'desc'));

    const unsubscribe = onSnapshot(
      q,
      async (snapshot) => {
        const tasks = snapshot.docs
          .map((d) => ({ id: d.id, ...d.data() }))
          .filter((task) => !hiddenHistory.includes(task.id));

        logger.log(`📋 Helper history loaded: ${tasks.length} items`, tasks);

        setCompletedTasks(tasks);

        const uniqueDomains = [...new Set(tasks.map((t) => t.domain || 'General'))];
        setDomains(uniqueDomains);

        const summaryStats = await calculateSummaryStats(currentUser.uid, 'helper');
        setStats(summaryStats);
      },
      (err) => {
        console.error('❌ Error listening to helper history:', err);
      }
    );

    return () => unsubscribe();
  }, [ready, currentUser, currentUserRole, hiddenHistory]);

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  // Soft delete (store taskId in helper's hiddenHistory array)
  const handleDelete = async (taskId) => {
    if (!currentUser) {
      console.warn('No current user - cannot hide task');
      return;
    }

    try {
      const userRef = doc(db, 'users', currentUser.uid);
      await updateDoc(userRef, {
        hiddenHistory: arrayUnion(taskId),
      });

      setHiddenHistory((prev) => [...prev, taskId]);
      setCompletedTasks((prev) => prev.filter((t) => t.id !== taskId));
    } catch (error) {
      console.error('Error hiding task:', error);
    }
  };

  const handleExportPDF = () => {
    const content = completedTasks
      .map((t) => `${t.title}\nCompleted: ${t.dateCompleted}\nDomain: ${t.domain}\n\n`)
      .join('\n');

    const blob = new Blob([`Completed Tasks History\n\n${content}`], {
      type: 'text/plain',
    });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `history-${new Date().toISOString().split('T')[0]}.txt`;
    a.click();
  };

  if (!ready || !currentUser || hiddenHistory === null) {
    return (
      <div className="flex min-h-screen items-center justify-center bg-slate-50">
        <div className="text-center">
          <div className="mx-auto mb-4 h-10 w-10 animate-spin rounded-full border-b-2 border-blue-600" />
          <p className="text-sm text-slate-500">Loading history…</p>
        </div>
      </div>
    );
  }

  return (
    <OperationsShell
      user={currentUser}
      roleLabel={getRoleLabel(currentUserRole)}
      onLogout={handleLogout}
      tabs={[{ id: "history", label: "Incident history" }]}
      activeTab="history"
      onTabChange={() => {}}
    >
      <IncidentHistoryView
        tasks={completedTasks}
        stats={stats}
        domains={domains}
        counterpartyLabel="Customer"
        counterpartyField="clientName"
        onDelete={handleDelete}
        onExport={handleExportPDF}
        emptyAction={{ label: "Go to console", onClick: () => navigate('/dashboard-helper') }}
      />
    </OperationsShell>
  );
}

export default HelperHistory;
